import { type EmailProblem, EmailProblem as EmailProblemSchema } from "@minga/protocol"
import { Schema } from "effect"
import { ApiError } from "@/lib/api"
import { formatRelative } from "@/lib/time"

/**
 * The words of the email UI (wave M spec §6.2). Never an error code on screen: every `EmailProblem` becomes one plain
 * sentence. The record is exhaustive, so a new problem literal breaks the build until it has words.
 */
export const EMAIL_PROBLEM_TEXT: Record<EmailProblem, string> = {
  not_configured: "Email isn't set up on this Minga yet.",
  suppressed: "This address can't get email from Minga (it bounced before, or was reported as spam).",
  daily_limit: "Minga has sent all the email it can today. Try again tomorrow.",
  busy: "Email is busy right now. Try again in a minute.",
  invalid: "Something was wrong with this email on our side.",
  failed: "The email didn't go out.",
  inviter_limit: "You've sent a lot of invite emails. Try again later.",
  workspace_limit: "This workspace has sent a lot of invite emails today. Try again tomorrow.",
  recipient_limit: "This address has had enough invite emails for today. Try again tomorrow.",
  resend_limit: "This invite was emailed 3 times already.",
  declined: "They declined invites to this workspace before, so we didn't email them.",
  unverified: "Your email address isn't verified yet, so Minga didn't email this invite.",
}

/** Wave E (spec §8): the invite sheet's line while the inviter's own address is unverified. */
export const VERIFY_TO_EMAIL_INVITES = "Verify your address to email invites · Copy link works now"

/** Said after every email that didn't go out: the invite link still works. */
export const COPY_FALLBACK = "Copy the link and send it yourself."

export const NOT_SET_UP_TEXT = EMAIL_PROBLEM_TEXT.not_configured

/** A refused reset-link request: 429 (too many from this IP) or anything else. Never says whether an account exists. */
export const resetRequestErrorText = (status: number | null | undefined): string =>
  status === 429
    ? "Too many requests from here. Try again in an hour."
    : "That didn't work. Check the address and try again."

/** The inline error under the note field (the protocol's `inviteNoteProblem`). */
export const noteProblemText = (problem: "too_long" | "has_link"): string =>
  problem === "too_long"
    ? "Keep the note under 280 characters."
    : "Notes can't contain links, email addresses or phone numbers."

const isProblem = Schema.is(EmailProblemSchema)

/** The `problem` of an error body, when it is one we have words for. */
export const problemOf = (error: unknown): EmailProblem | null => {
  const problem = error instanceof ApiError ? error.body?.problem : undefined
  return isProblem(problem) ? problem : null
}

/** The `error` of an email route's error body ("note_has_link", "resend_limit", …), or null. */
export const routeErrorOf = (error: unknown): string | null => {
  const code = error instanceof ApiError ? error.body?.error : undefined
  return typeof code === "string" ? code : null
}

/** POST /me/test-email failed: 429 rate limited, 503 not set up, 502 the email didn't go out (with its problem). */
export const testEmailErrorText = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 429) return "You've sent enough test emails for now. Try again later."
    if (error.status === 503) return NOT_SET_UP_TEXT
    if (error.status === 502) return EMAIL_PROBLEM_TEXT[problemOf(error) ?? "failed"]
    if (error.status === 0) return "Couldn't reach Minga. Try again."
  }
  return EMAIL_PROBLEM_TEXT.failed
}

/**
 * A refused Resend, in words. 429 `resend_limit` says when it works again (or that it never will); 409 means the
 * invitation changed under us (the caller refreshes the list).
 */
export const resendErrorText = (error: unknown, now: number = Date.now()): string => {
  const code = routeErrorOf(error)
  if (code === "resend_limit") {
    const at = error instanceof ApiError ? error.body?.resendAt : null
    return typeof at === "number"
      ? `You can resend this ${formatRelative(new Date(at), now)}.`
      : `${EMAIL_PROBLEM_TEXT.resend_limit} ${COPY_FALLBACK}`
  }
  if (code === "invitation_expired") return "This invite has expired. Renew it instead."
  if (code === "invitation_not_pending") return "This invite was already accepted, declined or revoked."
  if (code === "not_configured") return NOT_SET_UP_TEXT
  if (code === "not_found") return "This invite is gone."
  if (error instanceof ApiError && error.status === 403) return "Only owners and admins can resend invites."
  if (error instanceof ApiError && error.status === 0) return "Couldn't reach Minga. Try again."
  return `${EMAIL_PROBLEM_TEXT.failed} ${COPY_FALLBACK}`
}

/** A refused invite-by-email (the invitation was not created), in words; Better Auth's codes come through as `error`. */
export const inviteByEmailErrorText = (error: unknown): string | null => {
  switch (routeErrorOf(error)) {
    case "note_too_long":
      return noteProblemText("too_long")
    case "note_has_link":
      return noteProblemText("has_link")
    case "unknown_project":
      return "That project isn't in this workspace any more. Pick another one, or none."
    case "invalid_body":
      return "Not a valid email"
    case "forbidden":
      return "Only owners and admins can invite people."
    default:
      return null
  }
}

/**
 * The email line of a pending invitation: "Emailed 1 h ago", "Sending email…", or why it didn't go out. When the
 * per-invitation rule says no Resend yet, it says when ("· you can resend in 1 h") or that it's used up.
 */
export const inviteEmailLine = (
  status: {
    readonly status: "sending" | "sent" | "failed" | "skipped"
    readonly problem: EmailProblem | null
    readonly at: number
    readonly sentCount?: number
    readonly canResend?: boolean
    readonly resendAt?: number | null
  },
  now: number = Date.now(),
): { readonly text: string; readonly tone: "ok" | "muted" | "blocked" } => {
  switch (status.status) {
    case "sent": {
      const emailed = `Emailed ${formatRelative(new Date(status.at), now)}`
      if (status.canResend !== false) return { text: emailed, tone: "muted" }
      if (typeof status.resendAt === "number")
        return {
          text: `${emailed} · you can resend ${formatRelative(new Date(status.resendAt), now)}`,
          tone: "muted",
        }
      return { text: `Emailed ${status.sentCount ?? 3} times. Copy the link instead.`, tone: "muted" }
    }
    case "sending":
      return { text: "Sending email…", tone: "muted" }
    default:
      return { text: EMAIL_PROBLEM_TEXT[status.problem ?? "failed"], tone: "blocked" }
  }
}
